import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Breadcrumbs, PageHeader } from '~/components/data';
import { useToast } from '~/components/feedback';
import { Button, DatePicker, Textarea } from '~/components/primitives';
import { renderApiErrorMessage } from '~/lib/api/error-renderer';
import {
  type StockReconciliationUpdateBody,
  fetchStockReconciliation,
  updateStockReconciliation,
} from '~/lib/api/stock-reconciliation';
import shared from './_auth._shared.module.css';

/**
 * EFE-9 — SR edit (header-only: posting_date + notes). Lines edit via existing
 * wizard `_auth.stock-reconciliation.new.tsx`. State guard: server enforces 409.
 */
export const Route = createFileRoute('/_auth/stock-reconciliation/$id/edit')({
  component: SrEdit,
});

function SrEdit(): JSX.Element {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['stock-reconciliation', 'detail', id],
    queryFn: () => fetchStockReconciliation(id),
  });

  const [form, setForm] = useState({
    posting_date: '',
    notes_ru: '',
  });
  const [primed, setPrimed] = useState(false);

  useEffect(() => {
    if (query.data && !primed) {
      const sr = query.data as unknown as {
        posting_datetime?: string;
        created_at: string;
        notes?: string | null;
      };
      setForm({
        posting_date: (sr.posting_datetime ?? sr.created_at).slice(0, 10),
        notes_ru: sr.notes ?? '',
      });
      setPrimed(true);
    }
  }, [query.data, primed]);

  const updateMut = useMutation({
    mutationFn: () => {
      const sr = query.data as unknown as { updated_at?: string; created_at?: string };
      const body: StockReconciliationUpdateBody = {
        posting_date: form.posting_date,
        notes_ru: form.notes_ru || null,
        // EFE-14: optimistic concurrency.
        expected_updated_at: sr.updated_at ?? sr.created_at,
      };
      return updateStockReconciliation(id, body);
    },
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.sr_toast_updated() });
      void qc.invalidateQueries({ queryKey: ['stock-reconciliation'] });
      void navigate({ to: '/stock-reconciliation/$id', params: { id } });
    },
    onError: (err: unknown) =>
      toast.push({
        variant: 'error',
        title: m.sr_toast_update_failed(),
        description: renderApiErrorMessage(err),
      }),
  });

  if (query.isLoading) return <div>…</div>;
  if (!query.data) return <div>Not found</div>;

  const sr = query.data as unknown as {
    voucher_no: string | null;
    status: 'draft' | 'submitted' | 'cancelled';
  };
  const readOnly = sr.status !== 'draft';

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: m.nav_stock_reconciliation(), to: '/stock-reconciliation' },
              { label: sr.voucher_no ?? id.slice(0, 8), to: `/stock-reconciliation/${id}` },
              { label: m.common_button_edit() },
            ]}
          />
        }
        title={m.sr_edit_title()}
      />
      {readOnly && (
        <div className={[shared.inlineAlert, shared.inlineAlertWarn].join(' ')} role="status">
          {m.error_entity_not_editable({
            current_state: sr.status,
            editable_states: 'draft',
          })}
        </div>
      )}
      <div className={shared.formColumn480}>
        <DatePicker
          label={m.stock_entry_field_posting_date()}
          value={form.posting_date || null}
          onChange={(next) => setForm({ ...form, posting_date: next ?? '' })}
          disabled={readOnly}
        />
        <Textarea
          label={m.stock_entry_field_notes()}
          value={form.notes_ru}
          onChange={(e) => setForm({ ...form, notes_ru: e.target.value })}
          disabled={readOnly}
          rows={3}
        />
      </div>
      <div className={shared.stickyFooter}>
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate({ to: '/stock-reconciliation/$id', params: { id } })}
        >
          {m.common_button_cancel()}
        </Button>
        <Button
          variant="primary"
          onClick={() => updateMut.mutate()}
          loading={updateMut.isPending}
          disabled={readOnly}
        >
          {m.common_button_save()}
        </Button>
      </div>
    </div>
  );
}
